import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import {
  useOrgGuestFeedbackIntegrationSidebarQuery,
  useGuestFeedbackIntegrationBrandsQuery,
  useGuestFeedbackIntegrationOpusLocationsQuery,
  useUpdateOrgGuestFeedbackIntegrationLocationMutation,
  useUpdateOrgGuestFeedbackIntegrationBrandMutation,
} from "./GuestFeedbackIntegrationDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import LinkIcon from "@src/lib/icons/18px/link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import Text from "@src/lib/ui/Text";
import { MapPinIcon, Check } from "lucide-react";
import { Button } from "@src/lib/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import { cn } from "@src/lib/utils/cn";
import { useToast } from "@src/lib/hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import { GraphqlOperations } from "@src/types.generated";
import { StringParam, useQueryParam } from "use-query-params";

type AddressFields = {
  lineOne: string;
  city: string;
  usState?: string | null;
  postalCode: string;
};

const formatAddress = (address?: AddressFields | null): string => {
  if (!address) return "";
  return [address.lineOne, address.city, address.usState, address.postalCode]
    .filter(Boolean)
    .join(", ");
};

type LocationOption = {
  id: number;
  name: string;
  address: string;
};

type Props = {
  rowId: string;
  showHeader?: boolean;
};

gql`
  query OrgGuestFeedbackIntegrationSidebar($id: UUID!) {
    OrgGuestFeedbackIntegration(id: $id) {
      id
      enabled
      googleBusinessConfig {
        id
        accountId
      }
      locationGroup {
        id
        name
      }
      ovationIntegrationConfig {
        id
        companyId
      }
      tattleIntegrationConfig {
        id
        merchantId
      }
      locations {
        id
        sourceLocationId
        sourceName
        sourceAddress {
          lineOne
          city
          usState
          postalCode
        }
        location {
          id
          name
          address {
            lineOne
            city
            usState
            postalCode
          }
        }
      }
    }
  }

  query GuestFeedbackIntegrationOpusLocations(
    $input: LocationsInput
    $pagination: PaginationInput
  ) {
    Locations(input: $input, pagination: $pagination) {
      objects {
        id
        name
        address {
          lineOne
          city
          usState
          postalCode
        }
      }
    }
  }

  query GuestFeedbackIntegrationBrands {
    LocationGroups(input: { types: [BRAND] }) {
      objects {
        id
        name
      }
    }
  }

  mutation UpdateOrgGuestFeedbackIntegrationBrand(
    $input: UpdateOrgGuestFeedbackIntegrationInput!
  ) {
    updateOrgGuestFeedbackIntegration(input: $input) {
      success
      error {
        message
      }
      orgGuestFeedbackIntegration {
        id
        locationGroup {
          id
          name
        }
      }
    }
  }

  mutation UpdateOrgGuestFeedbackIntegrationLocation(
    $input: UpdateOrgGuestFeedbackIntegrationLocationInput!
  ) {
    updateOrgGuestFeedbackIntegrationLocation(input: $input) {
      success
      error {
        message
      }
      orgGuestFeedbackIntegrationLocation {
        id
        location {
          id
          name
          address {
            lineOne
            city
            usState
            postalCode
          }
        }
      }
    }
  }
`;

// Searchable location combobox sub-component
const LocationCombobox: React.FC<{
  value: number | null;
  options: LocationOption[];
  selected?: LocationOption | null;
  onChange: (value: number) => void;
}> = ({ value, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(
      (opt) =>
        opt.name.toLowerCase().includes(searchLower) ||
        opt.address.toLowerCase().includes(searchLower),
    );
  }, [options, search]);

  const selectedOption = useMemo(() => {
    if (value === null) return null;
    return options.find((opt) => opt.id === value) ?? selected ?? null;
  }, [value, options, selected]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-8 w-full justify-between py-1 font-normal"
        >
          <span
            className={cn(
              "flex min-w-0 flex-col items-start",
              !selectedOption && "text-text-secondary",
            )}
          >
            <span className="truncate">
              {selectedOption ? selectedOption.name : "Select location..."}
            </span>
            {selectedOption?.address ? (
              <span className="truncate text-text-secondary">
                {selectedOption.address}
              </span>
            ) : null}
          </span>
          <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search locations..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No locations found</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={String(opt.id)}
                  onSelect={() => {
                    onChange(opt.id);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 size-4 shrink-0",
                      value === opt.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{opt.name}</span>
                    {opt.address ? (
                      <span className="truncate text-text-secondary">
                        {opt.address}
                      </span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const GuestFeedbackIntegrationDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const { addToast, addErrorToast } = useToast();
  const [integrationTab, setIntegrationTab] = useQueryParam(
    "integrationTab",
    StringParam,
  );
  const selectedTab = integrationTab === "locations" ? "locations" : "details";
  const { data } = useOrgGuestFeedbackIntegrationSidebarQuery({
    variables: { id: rowId },
    skip: !rowId,
  });

  const { data: brandsData } = useGuestFeedbackIntegrationBrandsQuery();
  const brandId = data?.OrgGuestFeedbackIntegration?.locationGroup?.id;
  const { data: locationsData } = useGuestFeedbackIntegrationOpusLocationsQuery(
    {
      skip: !data?.OrgGuestFeedbackIntegration,
      variables: {
        pagination: { limit: 2000 },
        input: {
          sort: { column: "name", descending: false },
          ...(brandId ? { locationGroupsChildOf: [brandId] } : {}),
        },
      },
    },
  );

  const [updateIntegrationBrand] =
    useUpdateOrgGuestFeedbackIntegrationBrandMutation({
      refetchQueries: [
        GraphqlOperations.Query.OrgGuestFeedbackIntegrationSidebar,
        GraphqlOperations.Query.OrgGuestFeedbackIntegrations,
        GraphqlOperations.Query.GuestFeedbackIntegrationOpusLocations,
      ],
      onCompleted: (result) => {
        if (result.updateOrgGuestFeedbackIntegration.success) {
          addToast({ message: "Brand updated" });
        } else {
          addErrorToast({
            message:
              result.updateOrgGuestFeedbackIntegration.error?.message ??
              "Failed to update brand",
            callsite: "guest_feedback_integration_brand_update",
          });
        }
      },
    });

  const [updateIntegrationLocation] =
    useUpdateOrgGuestFeedbackIntegrationLocationMutation({
      refetchQueries: [
        GraphqlOperations.Query.OrgGuestFeedbackIntegrationSidebar,
        GraphqlOperations.Query.OrgGuestFeedbackIntegrations,
      ],
      onCompleted: (result) => {
        if (result.updateOrgGuestFeedbackIntegrationLocation.success) {
          addToast({ message: "Location updated" });
        } else {
          addErrorToast({
            message:
              result.updateOrgGuestFeedbackIntegrationLocation.error?.message ??
              "Failed to update location",
            callsite: "guest_feedback_integration_location_update",
          });
        }
      },
    });

  const integration = data?.OrgGuestFeedbackIntegration;

  const locationOptions = useMemo(() => {
    return (
      locationsData?.Locations?.objects?.map((loc) => ({
        id: loc.id,
        name: loc.name,
        address: formatAddress(loc.address),
      })) ?? []
    );
  }, [locationsData]);

  const brandOptions = useMemo(() => {
    return brandsData?.LocationGroups?.objects ?? [];
  }, [brandsData]);

  const handleBrandChange = (value: string) => {
    if (!integration) return;
    updateIntegrationBrand({
      variables: {
        input: {
          id: integration.id,
          enabled: integration.enabled,
          locationGroupId: value === "none" ? null : value,
        },
      },
    });
  };

  const handleLocationChange = (
    integrationLocationId: string,
    locationId: number,
  ) => {
    updateIntegrationLocation({
      variables: {
        input: {
          id: integrationLocationId,
          locationId,
        },
      },
    });
  };

  const locationsCount = integration?.locations?.length;

  if (!integration) return null;

  const integrationType = integration.googleBusinessConfig
    ? "Google Business"
    : integration.ovationIntegrationConfig
      ? "Ovation"
      : integration.tattleIntegrationConfig
        ? "Tattle"
        : "Unknown";

  const detailsItems: InfoTableItem[] = [
    {
      label: "Integration ID",
      value: integration.id,
    },
    {
      label: "Type",
      value: integrationType,
    },
    {
      label: "Status",
      value: integration.enabled ? "Enabled" : "Disabled",
    },
  ];

  if (integration.googleBusinessConfig) {
    detailsItems.push({
      label: "Google Business Account ID",
      value: integration.googleBusinessConfig.accountId || "--",
    });
  }

  if (integration.ovationIntegrationConfig) {
    detailsItems.push({
      label: "Ovation Company ID",
      value: integration.ovationIntegrationConfig.companyId || "--",
    });
  }

  if (integration.tattleIntegrationConfig) {
    detailsItems.push({
      label: "Tattle Merchant ID",
      value: integration.tattleIntegrationConfig.merchantId || "--",
    });
  }

  const sourceLocationLabel = integration.ovationIntegrationConfig
    ? "Ovation Location"
    : integration.googleBusinessConfig
      ? "Google Location"
      : integration.tattleIntegrationConfig
        ? "Tattle Location"
        : "Source Location";

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={integrationType}
        icon={<LinkIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.GuestFeedbackIntegration}
      />
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <Tabs value={selectedTab} onValueChange={setIntegrationTab}>
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="locations">
                Locations{locationsCount ? ` (${locationsCount})` : ""}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="details">
              <InfoTable items={detailsItems} />
              <div className="mt-4 flex flex-col gap-1">
                <Text variant="compact-description">Brand</Text>
                <Select
                  value={integration.locationGroup?.id ?? "none"}
                  onValueChange={handleBrandChange}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All locations</SelectItem>
                    {brandOptions.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent value="locations">
              {!integration.locations || integration.locations.length === 0 ? (
                <div className="p-4">
                  <div className="text-gray-7">No locations configured</div>
                </div>
              ) : (
                <div className="p-4">
                  <HtmlTable className="w-full">
                    <HtmlTableHeader>
                      <HtmlTableRow>
                        <HtmlTableHead>{sourceLocationLabel}</HtmlTableHead>
                        <HtmlTableHead>Opus Location</HtmlTableHead>
                      </HtmlTableRow>
                    </HtmlTableHeader>
                    <HtmlTableBody>
                      {integration.locations.map((loc) => (
                        <HtmlTableRow key={loc.id}>
                          <HtmlTableCell>
                            <div className="flex items-start gap-2">
                              <MapPinIcon className="mt-0.5 size-4 shrink-0 text-icon-secondary" />
                              <div className="flex min-w-0 flex-col">
                                <Text variant="compact-title">
                                  {loc.sourceName || loc.sourceLocationId}
                                </Text>
                                {formatAddress(loc.sourceAddress) ? (
                                  <Text
                                    variant="compact-description"
                                    className="text-text-secondary"
                                  >
                                    {formatAddress(loc.sourceAddress)}
                                  </Text>
                                ) : null}
                              </div>
                            </div>
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <LocationCombobox
                              value={loc.location?.id ?? null}
                              options={locationOptions}
                              selected={
                                loc.location
                                  ? {
                                      id: loc.location.id,
                                      name: loc.location.name,
                                      address: formatAddress(
                                        loc.location.address,
                                      ),
                                    }
                                  : null
                              }
                              onChange={(locationId) =>
                                handleLocationChange(loc.id, locationId)
                              }
                            />
                          </HtmlTableCell>
                        </HtmlTableRow>
                      ))}
                    </HtmlTableBody>
                  </HtmlTable>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SidebarContent>
    </div>
  );
};

export default GuestFeedbackIntegrationDetailSidebar;
